import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, Search, Download, Loader2, ShieldCheck, MapPin, Building2, CheckCircle2, AlertCircle, Zap, ChevronDown, ChevronUp } from "lucide-react";

export default function AdminSirene() {
  const { logout } = useAuth();
  const [form, setForm] = useState({ q: "", city: "", postal_code: "", siret: "" });
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [selected, setSelected] = useState({}); // siret -> bool
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [autoEnrich, setAutoEnrich] = useState(true);

  // Bulk regional import
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDepts, setBulkDepts] = useState(["75", "77", "78", "91", "92", "93", "94", "95"]);
  const [bulkPreset, setBulkPreset] = useState("idf");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [enrichProgress, setEnrichProgress] = useState(null);

  const REGION_PRESETS = {
    idf: { label: "Île-de-France", depts: ["75","77","78","91","92","93","94","95"] },
    paca: { label: "PACA", depts: ["04","05","06","13","83","84"] },
    "auvergne-rhone-alpes": { label: "Auvergne-Rhône-Alpes", depts: ["01","03","07","15","26","38","42","43","63","69","73","74"] },
    occitanie: { label: "Occitanie", depts: ["09","11","12","30","31","32","34","46","48","65","66","81","82"] },
    "nouvelle-aquitaine": { label: "Nouvelle-Aquitaine", depts: ["16","17","19","23","24","33","40","47","64","79","86","87"] },
    "hauts-de-france": { label: "Hauts-de-France", depts: ["02","59","60","62","80"] },
    "grand-est": { label: "Grand Est", depts: ["08","10","51","52","54","55","57","67","68","88"] },
    bretagne: { label: "Bretagne", depts: ["22","29","35","56"] },
    normandie: { label: "Normandie", depts: ["14","27","50","61","76"] },
    "pays-de-la-loire": { label: "Pays de la Loire", depts: ["44","49","53","72","85"] },
  };

  const applyPreset = (key) => {
    setBulkPreset(key);
    setBulkDepts(REGION_PRESETS[key].depts);
  };

  const IDF_DEPTS = [
    { code: "75", name: "Paris" },
    { code: "77", name: "Seine-et-Marne" },
    { code: "78", name: "Yvelines" },
    { code: "91", name: "Essonne" },
    { code: "92", name: "Hauts-de-Seine" },
    { code: "93", name: "Seine-Saint-Denis" },
    { code: "94", name: "Val-de-Marne" },
    { code: "95", name: "Val-d'Oise" },
  ];

  const toggleDept = (code) => {
    setBulkDepts((prev) => prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code]);
  };

  const runBulkImport = async () => {
    if (bulkDepts.length === 0) return;
    setBulkRunning(true);
    setBulkResult(null);
    setEnrichProgress(null);
    try {
      const { data } = await api.post("/admin/sirene/bulk-import", {
        departments: bulkDepts,
        max_per_department: 1000,
      });
      setBulkResult(data);
      // Optional auto-enrich loop
      if (autoEnrich && data.total_inserted > 0) {
        setEnrichProgress({ done: 0, total: data.total_inserted, batches: 0 });
        let cumulative = 0;
        // Loop calls until remaining=0
        for (let i = 0; i < 200; i++) {
          const { data: e } = await api.post("/admin/companies/enrich-all-missing?limit=25");
          cumulative += e.processed;
          setEnrichProgress({ done: cumulative, total: cumulative + (e.remaining || 0), batches: i + 1, last: e });
          if (!e.remaining || e.processed === 0) break;
        }
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Erreur lors de l'import massif");
    } finally {
      setBulkRunning(false);
    }
  };

  const search = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    setResults([]);
    setSuccess(null);
    try {
      const { data } = await api.post("/admin/sirene/search", {
        q: form.q || null,
        city: form.city || null,
        postal_code: form.postal_code || null,
        siret: form.siret || null,
        per_page: 25,
        page: 1,
      });
      setResults(data.results || []);
      setTotalResults(data.total_results || 0);
      // pre-select all not-yet-imported
      const sel = {};
      (data.results || []).forEach((r) => { if (!r.already_imported && r.siret) sel[r.siret] = true; });
      setSelected(sel);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Erreur de recherche");
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = (val) => {
    const sel = {};
    results.forEach((r) => { if (!r.already_imported && r.siret) sel[r.siret] = val; });
    setSelected(sel);
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const importSelected = async () => {
    const items = results
      .filter((r) => selected[r.siret] && !r.already_imported)
      .map((r) => ({
        name: r.name,
        siret: r.siret,
        address: r.address,
        city: r.city,
        postal_code: r.postal_code,
        department: r.department ? `${r.department}` : "",
        legal_form: r.legal_form || "",
      }));
    if (items.length === 0) return;
    setImporting(true);
    setError("");
    try {
      const { data } = await api.post("/admin/sirene/import", { items, skip_duplicates: true });
      let enrichedCount = 0;
      if (autoEnrich && data.inserted?.length) {
        // Enrich each inserted company via Google
        for (const item of data.inserted) {
          try {
            await api.post(`/admin/companies/${item.id}/enrich-google`);
            enrichedCount++;
          } catch { /* skip */ }
        }
      }
      setSuccess({ ...data, enriched_count: enrichedCount });
      // re-run search to reflect already_imported state
      setTimeout(() => search(), 500);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Erreur d'import");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FB]">
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="container mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="eyebrow bg-[#2BD391] text-white px-3 py-1 rounded-full">Admin</span>
            <h1 className="font-heading font-extrabold text-xl tracking-tight text-[#2C3E50]">Import Sirene (INSEE)</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/companies" className="text-sm font-semibold text-[#2C3E50] hover:text-[#2BD391]"><Building2 className="w-4 h-4 inline mr-1.5" />Entreprises</Link>
            <Link to="/admin" className="text-sm font-semibold text-[#2C3E50] hover:text-[#2BD391]" data-testid="back-admin">
              <ArrowLeft className="w-4 h-4 inline" /> Dashboard
            </Link>
            <button onClick={logout} className="text-sm font-semibold hover:text-red-600">Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-10">
        <div className="bg-gradient-to-br from-[#E8FBF3] to-white border border-[#2BD391] rounded-3xl p-6 md:p-8 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#2BD391] rounded-full flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading font-extrabold text-xl text-[#2C3E50] mb-1">Source officielle française</h2>
              <p className="text-sm text-[#6B7280]">
                Données issues de l'API <b>Recherche d'entreprises</b> (data.gouv.fr / INSEE Sirene). Toutes les entreprises avec le code NAF <b>49.42Z</b> (Services de déménagement). Recherchez par ville, code postal, nom ou SIRET, puis sélectionnez celles à importer dans votre annuaire.
              </p>
            </div>
          </div>
        </div>

        {/* ===== BULK REGIONAL IMPORT ===== */}
        <div className="bg-white border border-[#E5E7EB] rounded-2xl mb-8 overflow-hidden" data-testid="bulk-region-card">
          <button onClick={() => setBulkOpen(!bulkOpen)} className="w-full px-6 py-5 flex items-center justify-between hover:bg-[#F7F9FB]">
            <div className="flex items-center gap-4 text-left">
              <div className="w-12 h-12 bg-gradient-to-br from-[#2BD391] to-[#1AA66C] rounded-full flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-heading font-extrabold text-[#2C3E50]">Import massif par région</div>
                <div className="text-sm text-[#6B7280]">Importez en un clic tous les déménageurs d'une zone (Île-de-France, Sud, etc.)</div>
              </div>
            </div>
            {bulkOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {bulkOpen && (
            <div className="px-6 pb-6 border-t border-[#F0F2F5] pt-5">
              <div className="text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-2">Choisir une région</div>
              <div className="flex flex-wrap gap-2 mb-5">
                {Object.entries(REGION_PRESETS).map(([key, val]) => (
                  <button
                    key={key}
                    onClick={() => applyPreset(key)}
                    className={`text-xs px-3 py-1.5 rounded-full transition-all ${bulkPreset === key ? "bg-[#2BD391] text-white" : "bg-[#F7F9FB] text-[#2C3E50] hover:bg-[#E8FBF3]"}`}
                    data-testid={`preset-${key}`}
                  >
                    {val.label} ({val.depts.length})
                  </button>
                ))}
              </div>

              <div className="text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-3">
                Départements à importer ({bulkDepts.length})
              </div>
              <div className="flex flex-wrap gap-2 mb-5 p-3 bg-[#F7F9FB] rounded-xl min-h-[3rem]">
                {bulkDepts.map((code) => (
                  <span key={code} className="inline-flex items-center gap-1.5 bg-white border border-[#2BD391] text-[#1AA66C] text-xs font-bold px-3 py-1 rounded-full">
                    {code}
                    <button onClick={() => setBulkDepts(bulkDepts.filter((x) => x !== code))} className="hover:text-red-600" aria-label="remove">×</button>
                  </span>
                ))}
                {bulkDepts.length === 0 && <span className="text-xs text-[#6B7280]">Sélectionnez une région ou saisissez les codes ci-dessous.</span>}
              </div>

              <div className="flex items-center gap-2 mb-5">
                <input
                  type="text"
                  placeholder="Ajouter un code département (ex: 38)"
                  className="input-square !rounded-full !py-2 text-sm max-w-xs"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const v = e.target.value.trim();
                      if (v && !bulkDepts.includes(v)) {
                        setBulkDepts([...bulkDepts, v]);
                        e.target.value = "";
                      }
                    }
                  }}
                  data-testid="bulk-add-dept"
                />
                <button onClick={() => setBulkDepts([])} className="text-xs uppercase tracking-widest text-[#6B7280] hover:underline">Vider</button>
              </div>

              <label className="flex items-center gap-2 text-sm text-[#2C3E50] cursor-pointer mb-4">
                <input type="checkbox" checked={autoEnrich} onChange={(e) => setAutoEnrich(e.target.checked)} className="accent-[#2BD391] w-4 h-4" />
                Enrichir automatiquement avec Google après l'import (téléphone, note, horaires...)
              </label>

              <button onClick={runBulkImport} disabled={bulkRunning || bulkDepts.length === 0} className="btn-primary disabled:opacity-50 disabled:pointer-events-none" data-testid="bulk-run-btn">
                {bulkRunning ? <><Loader2 className="w-4 h-4 animate-spin" /> Import en cours…</> : <><Zap className="w-4 h-4" /> Lancer l'import massif ({bulkDepts.length} dépt.)</>}
              </button>

              {bulkRunning && (
                <p className="text-xs text-[#6B7280] mt-3">⏳ L'import récupère toutes les entreprises de chaque département (jusqu'à 1000/dépt). L'enrichissement Google peut prendre plusieurs minutes selon le nombre.</p>
              )}

              {bulkResult && (
                <div className="mt-5 bg-[#E8FBF3] border border-[#2BD391] rounded-xl p-4" data-testid="bulk-result">
                  <div className="font-bold text-[#1AA66C] mb-2">✓ Import terminé</div>
                  <div className="text-sm text-[#2C3E50] mb-3">
                    <b>{bulkResult.total_inserted}</b> entreprises importées · {bulkResult.total_skipped} ignorées
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    {bulkResult.by_department.map((d) => (
                      <div key={d.department} className="bg-white rounded-lg px-3 py-2">
                        <div className="font-bold">Dépt {d.department}</div>
                        <div className="text-[#1AA66C]">+{d.inserted}</div>
                        {d.skipped > 0 && <div className="text-[#6B7280] text-[10px]">{d.skipped} déjà présent(s)</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {enrichProgress && (
                <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="enrich-progress">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-bold text-blue-900 text-sm">
                      {enrichProgress.last?.remaining ? "🔄 Enrichissement Google en cours…" : "✓ Enrichissement terminé"}
                    </div>
                    <div className="text-xs text-blue-700">{enrichProgress.done} traitées</div>
                  </div>
                  <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 transition-all" style={{ width: `${enrichProgress.total ? (enrichProgress.done / enrichProgress.total) * 100 : 0}%` }}></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <form onSubmit={search} className="bg-white rounded-2xl border border-[#E5E7EB] p-6 mb-8" data-testid="sirene-search-form">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3">
            <div className="md:col-span-4 relative">
              <label className="block text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-2">Nom ou mot-clé</label>
              <input className="input-square" placeholder="ex: Démépro" value={form.q} onChange={(e) => setForm({ ...form, q: e.target.value })} data-testid="sirene-q" />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-2">Ville</label>
              <input className="input-square" placeholder="ex: Paris" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="sirene-city" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-2">Code postal</label>
              <input className="input-square" placeholder="75011" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="sirene-postal" />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-2">SIRET / SIREN précis</label>
              <input className="input-square" placeholder="14 ou 9 chiffres" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} data-testid="sirene-siret" />
            </div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3 mt-4">
            <p className="text-xs text-[#6B7280]">💡 Conseil : indiquez une ville ou un code postal pour limiter les résultats (sinon trop de résultats).</p>
            <button type="submit" disabled={loading} className="btn-primary" data-testid="sirene-search-btn">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Recherche…</> : <><Search className="w-4 h-4" /> Rechercher</>}
            </button>
          </div>
        </form>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="sirene-error">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {success && (
          <div className="bg-[#E8FBF3] border border-[#2BD391] rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="sirene-success">
            <CheckCircle2 className="w-5 h-5 text-[#1AA66C] flex-shrink-0" />
            <div className="text-sm text-[#1AA66C]">
              <b>{success.inserted_count}</b> entreprise{success.inserted_count > 1 ? "s" : ""} importée{success.inserted_count > 1 ? "s" : ""}
              {success.skipped_count > 0 && <>, {success.skipped_count} ignorée{success.skipped_count > 1 ? "s" : ""} (déjà importée{success.skipped_count > 1 ? "s" : ""})</>}
              {success.enriched_count > 0 && <> · ✨ <b>{success.enriched_count}</b> enrichie{success.enriched_count > 1 ? "s" : ""} via Google</>}
              .
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="font-heading font-bold text-[#2C3E50]">{results.length} résultats affichés</div>
                <div className="text-xs text-[#6B7280]">{totalResults} entreprises au total trouvées</div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 text-xs text-[#2C3E50] cursor-pointer">
                  <input type="checkbox" checked={autoEnrich} onChange={(e) => setAutoEnrich(e.target.checked)} className="accent-[#2BD391] w-4 h-4" data-testid="auto-enrich-toggle" />
                  Enrichir auto avec Google après import
                </label>
                <button onClick={() => toggleAll(true)} className="text-xs uppercase tracking-widest text-[#2BD391] hover:underline" data-testid="select-all">Tout sélectionner</button>
                <button onClick={() => toggleAll(false)} className="text-xs uppercase tracking-widest text-[#6B7280] hover:underline" data-testid="deselect-all">Tout désélectionner</button>
                <button onClick={importSelected} disabled={selectedCount === 0 || importing} className="btn-primary disabled:opacity-50 disabled:pointer-events-none" data-testid="sirene-import-btn">
                  {importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Import…</> : <><Download className="w-4 h-4" /> Importer ({selectedCount})</>}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F7F9FB]">
                  <tr className="text-left">
                    <th className="px-4 py-3 w-10"></th>
                    <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold text-[#2C3E50]">Entreprise</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold text-[#2C3E50]">SIRET</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold text-[#2C3E50]">Adresse</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold text-[#2C3E50]">Forme</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold text-[#2C3E50]">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.siret || r.name} className={`border-t border-[#E5E7EB] ${r.already_imported ? "bg-[#F7F9FB] opacity-60" : "hover:bg-[#F7F9FB]"}`} data-testid={`sirene-row-${r.siret}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          disabled={r.already_imported || !r.siret}
                          checked={!!selected[r.siret]}
                          onChange={(e) => setSelected({ ...selected, [r.siret]: e.target.checked })}
                          className="accent-[#2BD391] w-4 h-4"
                          data-testid={`sirene-check-${r.siret}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-[#2C3E50]">{r.name}</div>
                        <div className="text-xs text-[#6B7280]">{r.raison_sociale}</div>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-[#6B7280]">{r.siret || "—"}</td>
                      <td className="px-4 py-3 text-xs">
                        <div className="flex items-start gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-[#2BD391] mt-0.5 flex-shrink-0" />
                          <div>
                            <div>{r.address || "—"}</div>
                            <div className="text-[#6B7280]">{r.city} {r.postal_code && `(${r.postal_code})`}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs">{r.legal_form || "—"}</td>
                      <td className="px-4 py-3">
                        {r.already_imported ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#E8FBF3] text-[#1AA66C] rounded-full">
                            <CheckCircle2 className="w-3 h-3" /> Déjà importée
                          </span>
                        ) : r.active ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full">Active</span>
                        ) : (
                          <span className="text-xs text-red-600">Inactive</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && results.length === 0 && !error && (
          <div className="text-center py-12 text-[#6B7280] text-sm" data-testid="sirene-empty">
            Lancez une recherche pour découvrir les entreprises officielles de déménagement.
          </div>
        )}
      </div>
    </div>
  );
}
